import React, { useState } from 'react';
import AnimatedSection from './AnimatedSection';

/**
 * Core transdisciplinarity overview content.
 *
 * By default it renders inside a white card with shadow.
 * Pass `withContainer={false}` to render only the inner content (no card),
 * and optionally provide a `className` for outer layout/styling.
 */
export const TransdisciplinarityOverview = ({ withContainer = true, className = '' }) => {
    const [isTransdisciplinarityOpen, setIsTransdisciplinarityOpen] = useState(false);
    const [isResearchOpen, setIsResearchOpen] = useState(false);
    const [isMethodologiesOpen, setIsMethodologiesOpen] = useState(false);
    const [isWhyOpen, setIsWhyOpen] = useState(false);

    const containerClasses = withContainer
        ? `bg-white rounded-lg shadow-md p-8 ${className}`.trim()
        : className;

    return (
        <div className={containerClasses}>
            <p className="text-lg text-gray-700 leading-relaxed text-justify mb-6">
                Transdisciplinary work is about building bridges between “ivory towers” and society through the synthesis of various modules and knowledge systems.
            </p>
            <div className="flex flex-wrap md:-mx-4 items-start">
                <div className="w-full md:w-1/2 md:px-4 mb-8">
                    <div
                        className={`p-6 border rounded-lg cursor-pointer transition-all duration-300 bg-white ${
                            isTransdisciplinarityOpen ? 'shadow-md' : 'shadow-sm hover:shadow-md'
                        }`}
                        onClick={() => setIsTransdisciplinarityOpen((prev) => !prev)}
                    >
                        <div className="flex items-center justify-between">
                            <h3 className="text-xl font-semibold text-gray-900">What is Transdisciplinarity?</h3>
                            <span className="text-2xl text-gray-500 select-none">
                                {isTransdisciplinarityOpen ? '−' : '+'}
                            </span>
                        </div>
                        <div
                            className={`mt-4 transition-all duration-300 ease-in-out ${
                                isTransdisciplinarityOpen ? 'opacity-100' : 'opacity-0 hidden'
                            }`}
                        >
                            <p className="text-lg text-gray-700 leading-relaxed text-justify">
                                Transdisciplinarity is a hands-on approach that blends academic theory with everyday insights to solve
                                problems that cannot be addressed through a single lens. Instead of remaining within one discipline,
                                scholars collaborate with individuals and professionals from diverse fields to tackle complex issues such as
                                climate change, healthcare gaps, and urban planning.
                            </p>
                        </div>
                    </div>
                </div>
                <div className="w-full md:w-1/2 md:px-4 mb-8">
                    <div
                        className={`p-6 border rounded-lg cursor-pointer transition-all duration-300 bg-white ${
                            isResearchOpen ? 'shadow-md' : 'shadow-sm hover:shadow-md'
                        }`}
                        onClick={() => setIsResearchOpen((prev) => !prev)}
                    >
                        <div className="flex items-center justify-between">
                            <h3 className="text-xl font-semibold text-gray-900">What is Transdisciplinary Research?</h3>
                            <span className="text-2xl text-gray-500 select-none">
                                {isResearchOpen ? '−' : '+'}
                            </span>
                        </div>
                        <div
                            className={`mt-4 transition-all duration-300 ease-in-out ${
                                isResearchOpen ? 'opacity-100' : 'opacity-0 hidden'
                            }`}
                        >
                            <p className="text-lg text-gray-700 leading-relaxed text-justify mb-4">
                                Transdisciplinary research is an innovative field of inquiry that transcends traditional academic
                                boundaries by integrating knowledge and methods from multiple disciplines. This collaborative framework
                                addresses complex issues that cannot be fully explored through a single academic lens.
                            </p>
                            <p className="text-lg text-gray-700 leading-relaxed text-justify mb-4">
                                By combining insights from fields such as science, the humanities, and the social sciences, transdisciplinary
                                research fosters a more holistic understanding of problems that are often attributed to specific disciplines.
                                This methodology encourages diverse perspectives, critical thinking, and creativity in addressing real-world
                                challenges.
                            </p>
                            <p className="text-lg text-gray-700 leading-relaxed text-justify">
                                In practice, transdisciplinary research involves interdisciplinary collaboration and communication to forge
                                academically rigorous solutions that address important issues from various perspectives. The outcome is a more
                                comprehensive understanding of the world and people around us, and a stronger foundation for addressing
                                challenges in society, the arts, science, and beyond.
                            </p>
                        </div>
                    </div>
                </div>
                <div className="w-full md:w-1/2 md:px-4 mb-8">
                    <div
                        className={`p-6 border rounded-lg cursor-pointer transition-all duration-300 bg-white ${
                            isWhyOpen ? 'shadow-md' : 'shadow-sm hover:shadow-md'
                        }`}
                        onClick={() => setIsWhyOpen((prev) => !prev)}
                    >
                        <div className="flex items-center justify-between">
                            <h3 className="text-xl font-semibold text-gray-900">Why this approach works</h3>
                            <span className="text-2xl text-gray-500 select-none">
                                {isWhyOpen ? '−' : '+'}
                            </span>
                        </div>
                        <div
                            className={`mt-4 transition-all duration-300 ease-in-out ${
                                isWhyOpen ? 'opacity-100' : 'opacity-0 hidden'
                            }`}
                        >
                            <ul className="list-disc pl-6 space-y-2 text-lg text-gray-700 leading-relaxed text-justify">
                                <li>
                                    <span className="font-semibold">Holistic Understanding.</span> By combining science, local knowledge,
                                    and practical skills, this approach integrates diverse epistemological perspectives to build a
                                    comprehensive understanding of complex issues.
                                </li>
                                <li>
                                    <span className="font-semibold">Real-World Applicability.</span> Solutions are rooted in real-world
                                    contexts, making them more likely to inform effective policy, practice, and decision-making.
                                </li>
                                <li>
                                    <span className="font-semibold">Enhanced Collaboration.</span> It encourages networking and cooperative
                                    efforts across disciplines and societal sectors, strengthening social and professional relationships.
                                </li>
                                <li>
                                    <span className="font-semibold">Effective Problem-Solving.</span> “Wicked” problems — marked by high
                                    complexity and social controversy — are addressed through evidence-informed, context-sensitive
                                    solutions.
                                </li>
                                <li>
                                    <span className="font-semibold">Empowerment of Stakeholders.</span> Engaging stakeholders throughout the
                                    research process ensures their insights and values are reflected, increasing legitimacy and ownership
                                    of outcomes.
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
                <div className="w-full md:w-1/2 md:px-4 mb-8">
                    <div
                        className={`p-6 border rounded-lg cursor-pointer transition-all duration-300 bg-white ${
                            isMethodologiesOpen ? 'shadow-md' : 'shadow-sm hover:shadow-md'
                        }`}
                        onClick={() => setIsMethodologiesOpen((prev) => !prev)}
                    >
                        <div className="flex items-center justify-between">
                            <h3 className="text-xl font-semibold text-gray-900">
                                Methodologies of Transdisciplinary Research
                            </h3>
                            <span className="text-2xl text-gray-500 select-none">
                                {isMethodologiesOpen ? '−' : '+'}
                            </span>
                        </div>
                        <div
                            className={`mt-4 transition-all duration-300 ease-in-out ${
                                isMethodologiesOpen ? 'opacity-100' : 'opacity-0 hidden'
                            }`}
                        >
                            <p className="text-lg text-gray-700 leading-relaxed text-justify mb-4">
                                Transdisciplinary research relies on a set of complementary methodologies that connect academic inquiry
                                with lived experience and practical action.
                            </p>
                            <ul className="list-disc pl-6 space-y-2 text-lg text-gray-700 leading-relaxed text-justify">
                                <li>
                                    <span className="font-semibold">Interdisciplinary Collaboration.</span> Bring together experts from
                                    different fields — such as teachers, doctors, engineers, and artists — to share methodologies and
                                    practices in order to tackle current issues.
                                </li>
                                <li>
                                    <span className="font-semibold">Stakeholder Participation.</span> Involve people directly affected by
                                    the issues — like community members, local leaders, or business owners — from research design through to
                                    analysis and dissemination.
                                </li>
                                <li>
                                    <span className="font-semibold">Curiosity-Driven Research.</span> Start with curiosity, not just data.
                                    Explore the “why” and “how” behind problems, even when that means venturing into uncharted territory,
                                    where surprising connections often lead to breakthroughs.
                                </li>
                                <li>
                                    <span className="font-semibold">Goal-Oriented Knowledge Creation.</span> Focus on creating tools,
                                    policies, or strategies people can actually use — prioritizing practical interventions over purely
                                    theoretical outcomes.
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

const TransdisciplinarityIntro = () => {
    return (
        <AnimatedSection
            className="mb-16"
            heading={
                <h2 className="text-3xl font-semibold text-gray-900 mb-8">
                    Transdisciplinarity
                </h2>
            }
        >
            <TransdisciplinarityOverview />
        </AnimatedSection>
    );
};

export default TransdisciplinarityIntro;

